import React from 'react';
import {Button, Form, Input, notification} from 'antd';
import Contact from '../model/Contact';

import {Item} from '../components/Views/Item';
import {ErrorForm, FormError} from './FormUtils';
import * as he from 'he';
import Flex from '../components/StyledComponents/Flex';
import './Forms.css';
import {isNullOrUndefined} from "../utils";
import {useTranslation} from "@nic-react-i18n/core";
import {useMutation} from "@tanstack/react-query";
import {useKeycloak} from "@nic-react/auth";
import {useLocation} from "react-router";
import {AppConf} from "../AppConf";
import {useAppendAuthorizationHeader} from "../utils/connector/api-connector";
import { ModalDataI } from './IpForm';


export interface ContactFields {
    name: string;
    email: string;
    phone: string;
}

interface ContactPostInterface {
    name: string;
    email: string;
    phone: string;
}

interface ContactFormInterface {
    contact?: Contact;
    readOnly: boolean;
    loading: boolean;
    errorInfo: FormError;
    handleCancel: (data?: ModalDataI) => void;
}

type ContactFormProps = ContactFormInterface

const formItemLayout = {
    labelCol: { span: 5 },
    wrapperCol: { span: 18 },
};

const phoneRegExp = /\+\d{1,3}\.\d{1,14}$/;

export const ContactForm: React.FC<ContactFormProps> = (props) =>  {
    const [origContact, setOrigContact] = React.useState<Contact>({} as Contact)

    const {t} = useTranslation()
    const {keycloak} = useKeycloak()
    const location = useLocation()
    const header = useAppendAuthorizationHeader()

    const [form] = Form.useForm()
    React.useEffect(() => {
        const {contact,  readOnly} = props;
        if (contact !== undefined && ! readOnly) {
            setOrigContact(contact)
            form.setFieldsValue({name: he.unescape(contact?.name??''), email: contact?.email??'', phone: contact?.phone??''});
        }
// eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])


    const updateContact = async (data: ContactFields) => {
        const {url:rootAPI} = AppConf.getRootUrl(keycloak.realm)

        const response = await fetch(`${rootAPI}${location.pathname}/contact`,
            {
                method: 'PUT',
                body: JSON.stringify(data),
                ...header
            })
        if (!response.ok) {
            throw new Error()
        }
        return response.json()
    }


    const valuesChanged = (newValues: ContactFields): boolean => {
        let res: boolean;
        res =
            (newValues.name !== origContact.name ) ||
            (newValues.phone !== origContact.phone) ||
            (newValues.email !== origContact.email);
        return res;
    }

    const {mutateAsync,  isLoading, isError, error } = useMutation(updateContact)




    const renderForm = (errorInfo: FormError)  => {
        const {loading, contact} = props;
        const action = isNullOrUndefined(contact) ? 'new' : 'modify';
        return (
            <div>
                <>
                    <Form form={form}>
                        <Form.Item
                            {...formItemLayout}
                            name={ 'name'}
                            label={t('contactForm.label.name')}
                            rules={[
                                {
                                    required: true,
                                    message: t('contactForm.message.name')
                                }]}
                        >


                            <Input
                                style={{minWidth: '40px'}}
                                autoComplete={'name'}
                                placeholder={t('contactForm.placeholder.name')}
                            />

                        </Form.Item>

                        <Form.Item
                            {...formItemLayout}
                            name={'email'}
                            label={t('contactForm.label.email')}
                            rules={[
                                {
                                    required: true, type: 'email',
                                    message: t('contactForm.message.email')
                                }
                            ]}
                        >
                            <Input
                                autoComplete={'email'}
                                placeholder={t('contactForm.placeholder.email')}
                            />
                        </Form.Item>
                        <Form.Item
                            {...formItemLayout}
                            label={t('contactForm.label.phone')}
                            name={'phone'}
                            rules={[
                                {
                                    required: true,
                                    pattern: phoneRegExp,
                                    message: t('contactForm.message.phone')
                                }
                            ]}
                        >

                            <Input autoComplete={'phone'} placeholder={'+39.0503151111'} />
                        </Form.Item>
                    </Form>
                    <div>
                        {isError &&
                            <ErrorForm
                                formKind={'contactForm'}
                                errorMessage={(error as Error).message}
                            />
                        }
                    </div>
                    <Flex
                        direction={'row'}
                        justify={'space-between'}
                        align={'center'}
                        className={'separator'}
                    >

                        <Button
                            size={'small'}
                            loading={isLoading}
                            onClick={() => {props.handleCancel(); }}
                        >
                            {t(`contactForm.button.${action}.cancel`)}
                        </Button>
                        <Button
                            size={'small'}
                            loading={loading}
                            onClick={() => {

                                form.validateFields()
                                    .then(() => {

                                        let fields: ContactFields = {} as ContactFields;
                                        let postFields: ContactPostInterface = {} as ContactPostInterface;

                                        fields.email = form.getFieldValue('email');
                                        fields.name = form.getFieldValue('name');

                                        fields.phone = form.getFieldValue('phone');

                                        postFields.name = fields.name;
                                        postFields.phone = fields.phone;
                                        postFields.email = fields.email;

                                        let valueChanged = valuesChanged(postFields);

                                        if (valueChanged) {
                                            mutateAsync(postFields).then(() => {
                                                notification.success(
                                                    {
                                                        message:
                                                            t('contactForm.success.modification.title'),
                                                        description:
                                                            t('contactForm.success.modification.description')
                                                    }
                                                );
                                            }).catch(() => {
                                                notification.error(
                                                    {
                                                        message:
                                                            t('contactForm.warning.modification.title'),
                                                        description:
                                                            t('contactForm.warning.modification.description')
                                                    }
                                                );
                                            }).finally(() => {
                                                const _contact = new Contact(postFields.name, postFields.email, postFields.phone)
                                                props.handleCancel({contact: _contact})
                                            })
                                        } else {
                                            notification.warning(
                                                {
                                                    message:
                                                        t('contactForm.warning.modification.title'),
                                                    description:
                                                        t('contactForm.warning.modification.description')
                                                }
                                            );
                                            // console.log('No values Changed');
                                        }

                                    })
                                    .catch(() => {
                                        notification.error(
                                            {
                                                message:
                                                    t('contactForm.error.modification.title'),
                                                description:
                                                    t('contactForm.error.modification.description')
                                            }
                                        )
                                    })

                            }}
                        >
                            {t(`contactForm.button.${action}.ok`)}
                        </Button>
                    </Flex>
                </>
            </div>
        );
    }

    const renderReadOnlyForm = () => {
        const {contact} = props;
        return (
            <div>
                <Item
                    label={t('contactForm.label.name')}
                    value={he.unescape(contact?.name ??  '')}
                />
                <Item
                    label={t('contactForm.label.email')}
                    value={contact?.email ?? ''}
                />
                <Item
                    label={t('contactForm.label.phone')}
                    value={contact?.phone ?? ''}
                />
                <Flex
                    direction={'row'}
                    justify={'flex-end'}
                    align={'center'}
                    className={'separator distance'}
                >

                    <Button
                        size={'small'}
                        onClick={() => {props.handleCancel(); }}
                    >
                        {t('contactForm.button.cancel')}
                    </Button>
                </Flex>
            </div>
        );
    }


    const {readOnly, errorInfo} = props;
    let component = null;
    if (readOnly)  {
        component = renderReadOnlyForm();
    } else {
        component = renderForm(errorInfo);
    }
    return (
        <div>
            {component}
        </div>
    );

}